/**
 * Lesson Editor illustration — miniature wireframe of the lesson editing interface:
 * step list with type badges, drag handles, and add step button.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function LessonEditor({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="132"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Lesson title bar */}
      <rect x="14" y="14" width="60" height="5" rx="2.5" fill={p.strong} opacity="0.3" />
      {/* Tab bar: Content | Settings */}
      <rect x="14" y="26" width="30" height="3" rx="1.5" fill={p.accent1} opacity="0.6" />
      <rect x="14" y="31" width="30" height="1" rx="0.5" fill={p.accent1} opacity="0.8" />
      <rect x="50" y="26" width="24" height="3" rx="1.5" fill={p.faint} />

      {/* "Learn" section label */}
      <rect x="14" y="40" width="18" height="2.5" rx="1.25" fill={p.faint} />

      {/* Step 1 — info type */}
      <rect
        x="14"
        y="48"
        width="92"
        height="18"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      {/* Drag handle dots */}
      <circle cx="20" cy="55" r="0.8" fill={p.medium} />
      <circle cx="20" cy="58" r="0.8" fill={p.medium} />
      <circle cx="23" cy="55" r="0.8" fill={p.medium} />
      <circle cx="23" cy="58" r="0.8" fill={p.medium} />
      {/* Step number */}
      <rect x="28" y="52" width="4" height="4" rx="2" fill={p.accent3} opacity="0.5" />
      {/* Type badge */}
      <rect x="35" y="52" width="14" height="4" rx="2" fill={p.accent3} opacity="0.25" />
      {/* Step title */}
      <rect x="28" y="59" width="50" height="2.5" rx="1.25" fill={p.subtle} />

      {/* Step 2 — MCQ type (selected/highlighted) */}
      <rect
        x="14"
        y="70"
        width="92"
        height="18"
        rx="4"
        fill={p.cardFill}
        stroke={p.accent1}
        strokeWidth="1"
        opacity="0.9"
      />
      <circle cx="20" cy="77" r="0.8" fill={p.medium} />
      <circle cx="20" cy="80" r="0.8" fill={p.medium} />
      <circle cx="23" cy="77" r="0.8" fill={p.medium} />
      <circle cx="23" cy="80" r="0.8" fill={p.medium} />
      <rect x="28" y="74" width="4" height="4" rx="2" fill={p.accent1} opacity="0.5" />
      <rect x="35" y="74" width="12" height="4" rx="2" fill={p.accent1} opacity="0.25" />
      <rect x="28" y="81" width="58" height="2.5" rx="1.25" fill={p.subtle} />

      {/* Step 3 — text type */}
      <rect
        x="14"
        y="92"
        width="92"
        height="18"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      <circle cx="20" cy="99" r="0.8" fill={p.medium} />
      <circle cx="20" cy="102" r="0.8" fill={p.medium} />
      <circle cx="23" cy="99" r="0.8" fill={p.medium} />
      <circle cx="23" cy="102" r="0.8" fill={p.medium} />
      <rect x="28" y="96" width="4" height="4" rx="2" fill={p.accent2} opacity="0.5" />
      <rect x="35" y="96" width="10" height="4" rx="2" fill={p.accent2} opacity="0.25" />
      <rect x="28" y="103" width="44" height="2.5" rx="1.25" fill={p.subtle} />

      {/* Add step button — dashed */}
      <rect
        x="14"
        y="116"
        width="92"
        height="12"
        rx="4"
        fill="none"
        stroke={p.cardStroke}
        strokeWidth="0.75"
        strokeDasharray="3 2"
      />
      {/* Plus icon */}
      <line
        x1="57"
        y1="119"
        x2="57"
        y2="125"
        stroke={p.medium}
        strokeWidth="1"
        strokeLinecap="round"
      />
      <line
        x1="54"
        y1="122"
        x2="60"
        y2="122"
        stroke={p.medium}
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}
